import { IBucketResponseSchema, MCustomSelectProps } from '@monetize/types/app';
import { DEFAULT_PAGER, TDataTablePager } from '@monetize/types/common';
import { useState } from 'react';
import { useGetListData } from '../../../../api/queryUtils';
import { MFlex, MText } from '../../../../components/Monetize';
import { MainSearchInputV2 } from '../../../../components/Monetize/MCustomSelect/components/MainSearchInputV2';
import { MCustomSelect } from '../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { Highlighter } from '../../../../components/Monetize/MPageSearchInput/Highlighter';
import { BUCKETS } from '../../../../constants';
import { useDebounce } from '../../../../hooks/useDebounce';
import { sortByCreateDate } from '../../../../utils';
import { ReferenceOptions } from '../../../Settings/rules-v3/rules.utils';

interface BucketSearchProps extends MCustomSelectProps {
  value: string | null | undefined;
  inputVariant: MCustomSelectProps['variant'];
  isSaveLoading: boolean;
  isReadOnly: boolean;
  onOpenBucketModal: () => void;
  onChange?: (value: string | null) => void;
}
const pager: TDataTablePager = {
  ...DEFAULT_PAGER,
  rows: 50,
  sortField: 'name',
  sortOrder: 1,
};

const renderItemContentForBucket: ReferenceOptions['renderItemContent'] = ({
  item,
  query,
}) => {
  return (
    <MFlex
      direction="column"
      overflow="hidden"
      whiteSpace="nowrap"
      textOverflow="ellipsis"
      w="100%"
      minW="200px"
    >
      <MFlex flexDir="column" w="100%">
        <Highlighter
          title={item.name}
          color="tPurple.dark"
          fontSize="sm"
          fontWeight="medium"
          textToHighlight={item.name}
          searchWords={[query]}
        />
        <MFlex alignItems="center" justifyContent="space-between">
          <Highlighter
            title={item.id}
            color="tGray.darkPurple"
            fontSize="xs"
            textToHighlight={item.id}
            searchWords={[query]}
          />
        </MFlex>
      </MFlex>
    </MFlex>
  );
};

export const BucketSearch = ({
  value,
  inputVariant,
  isSaveLoading,
  isReadOnly,
  onOpenBucketModal,
  ...rest
}: BucketSearchProps) => {
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebounce(query, 500);

  const { data: bucketList } = useGetListData<
    IBucketResponseSchema,
    IBucketResponseSchema[]
  >(
    'productCatalogBuckets',
    {
      config: pager,
      filters: {
        name: { contains: debouncedQuery },
      },
    },
    {
      select: (currentData) => {
        // sort by creation date
        return currentData.content.sort((itemA, itemB) =>
          sortByCreateDate(itemA, itemB),
        );
      },
    },
  );

  if (isReadOnly && bucketList) {
    const bucket = bucketList?.find((addr) => addr.id === value);
    if (bucket) {
      return <MText>{bucket.name}</MText>;
    }
    return (
      <MText color="tGray.darkPurple" fontStyle="italic">
        Blank
      </MText>
    );
  }

  return (
    <MCustomSelect
      name="bucketSelect"
      placeholder="Select a bucket"
      items={bucketList}
      value={value || null}
      prependItems={[
        {
          item: BUCKETS.NEW_BUCKET_OPTION,
          isAction: true,
          hasDivider: true,
          onClick: ({ onClose }) => {
            onClose && onClose();
            onOpenBucketModal();
          },
        },
      ]}
      itemValue="id"
      itemTitle="name"
      clearable
      onChangeQuery={setQuery}
      externalQuery={query}
      useExternalQuery
      useMainInputAsSearchInput
      MainInputComponent={MainSearchInputV2}
      renderItemContent={renderItemContentForBucket}
      isDisabled={isSaveLoading}
      isReadOnly={isReadOnly}
      variant={inputVariant}
      isSelectOpen={false}
      {...rest}
    />
  );
};
